import React, { useEffect, useMemo, useState } from "react";
import { useAppStore } from "../store/useAppStore";
import Pagination from "./Pagination";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SORT_OPTIONS = [
  { value: "createdAt:desc", label: "Сначала новые" },
  { value: "createdAt:asc", label: "Сначала старые" },
  { value: "updatedAt:desc", label: "Обновленные сверху" },
  { value: "updatedAt:asc", label: "Обновленные снизу" },
];

const sentenceKey = (sentence) => {
  return String(sentence?.documentId || sentence?.id || "");
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const pad = (num) => String(num).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeMediaUrl = (url, apiBase) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${String(apiBase || "").replace(/\/$/, "")}${url}`;
};

const normalizeRelationArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (Array.isArray(value?.data)) {
    return value.data
      .map((item) => (item?.attributes ? { id: item.id, ...item.attributes } : item))
      .filter(Boolean);
  }
  if (value?.data?.attributes) {
    return [{ id: value.data.id, ...value.data.attributes }];
  }
  if (value && typeof value === "object") return [value];
  return [];
};

const extractTranslationSignDocumentIds = (sentence) => {
  const translations = Array.isArray(sentence?.translations) ? sentence.translations : [];
  const ordered = [];

  translations.forEach((translation) => {
    const directEntries = Array.isArray(translation?.data?.signs) ? translation.data.signs : [];
    directEntries.forEach((value) => {
      if (value == null || value === "") return;
      if (typeof value === "string" && value.trim().toLowerCase().startsWith("p:")) return;
      if (typeof value === "number" && Number.isFinite(value)) {
        ordered.push(String(value));
        return;
      }
      const trimmed = String(value).trim();
      if (trimmed) ordered.push(trimmed);
    });
    if (ordered.length) return;

    const signs = normalizeRelationArray(translation?.signs);
    signs.forEach((sign) => {
      const documentId = sign?.documentId ?? sign?.document_id;
      if (documentId) ordered.push(String(documentId));
    });
  });

  return ordered;
};

const normalizeSignEntity = (item) => {
  const source = item?.attributes ? { id: item.id, ...item.attributes } : item;
  if (!source) return null;
  return {
    ...source,
    sign_videos: normalizeRelationArray(source.sign_videos),
  };
};

const normalizeMediaFiles = (value) => {
  return normalizeRelationArray(value)
    .map((item) => (item?.attributes ? { id: item.id, ...item.attributes } : item))
    .filter((item) => item?.url);
};

const SentencesPage = () => {
  const sentences = useAppStore((state) => state.sentences);
  const sentencesLoading = useAppStore((state) => state.sentencesLoading);
  const sentencesError = useAppStore((state) => state.sentencesError);
  const sentencesMutationLoading = useAppStore((state) => state.sentencesMutationLoading);
  const sentencesMutationError = useAppStore((state) => state.sentencesMutationError);
  const sentencesPagination = useAppStore((state) => state.sentencesPagination);
  const sentencesCreateLoading = useAppStore((state) => state.sentencesCreateLoading);
  const sentencesCreateError = useAppStore((state) => state.sentencesCreateError);
  const sentencesCreateReport = useAppStore((state) => state.sentencesCreateReport);
  const apiBase = useAppStore((state) => state.API);
  const token = useAppStore((state) => state.token);
  const signVideosById = useAppStore((state) => state.signVideosById);
  const fetchSignVideosByIds = useAppStore((state) => state.fetchSignVideosByIds);
  const fetchSentences = useAppStore((state) => state.fetchSentences);
  const updateSentence = useAppStore((state) => state.updateSentence);
  const createSentence = useAppStore((state) => state.createSentence);
  const createSentencesBulk = useAppStore((state) => state.createSentencesBulk);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("createdAt:desc");
  const [translationFilter, setTranslationFilter] = useState("all");
  const [draftsByKey, setDraftsByKey] = useState({});
  const [editingByKey, setEditingByKey] = useState({});
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [createMode, setCreateMode] = useState("single");
  const [singleText, setSingleText] = useState("");
  const [singleLanguage, setSingleLanguage] = useState("ru_RU");
  const [singleView, setSingleView] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [bulkLanguage, setBulkLanguage] = useState("ru_RU");
  const [bulkView, setBulkView] = useState("");
  const [activePlayback, setActivePlayback] = useState(null);
  const [playbackLoadingKey, setPlaybackLoadingKey] = useState("");
  const [playbackErrorByKey, setPlaybackErrorByKey] = useState({});

  useEffect(() => {
    fetchSentences(currentPage, itemsPerPage, {
      sort: sortBy,
      translationFilter,
    });
  }, [fetchSentences, currentPage, itemsPerPage, sortBy, translationFilter]);

  useEffect(() => {
    setDraftsByKey({});
    setEditingByKey({});
    setActivePlayback(null);
    setPlaybackLoadingKey("");
    setPlaybackErrorByKey({});
  }, [currentPage, itemsPerPage, sortBy, translationFilter]);

  const totalPages = useMemo(() => {
    return Math.max(1, sentencesPagination?.pageCount || 1);
  }, [sentencesPagination]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handlePageSizeChange = (event) => {
    const value = Number(event.target.value);
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value || "createdAt:desc");
    setCurrentPage(1);
  };

  const getDraft = (sentence) => {
    const key = sentenceKey(sentence);
    const draft = draftsByKey[key] || {};
    return {
      text: draft.text ?? sentence?.text ?? "",
      language: draft.language ?? sentence?.language ?? "",
      view: draft.view ?? (sentence?.view ?? ""),
    };
  };

  const handleDraftChange = (sentence, field, value) => {
    const key = sentenceKey(sentence);
    if (!key) return;

    setDraftsByKey((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const handleResetDraft = (sentence) => {
    const key = sentenceKey(sentence);
    if (!key) return;

    setDraftsByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSaveSentence = async (sentence) => {
    const key = sentenceKey(sentence);
    if (!key) return;

    const draft = getDraft(sentence);
    const ok = await updateSentence(sentence, {
      text: draft.text,
      language: draft.language,
      view: draft.view,
    });

    if (!ok) return;
    handleResetDraft(sentence);
    setEditingByKey((prev) => ({
      ...prev,
      [key]: false,
    }));
  };

  const handleStartEdit = (sentence) => {
    const key = sentenceKey(sentence);
    if (!key) return;
    setEditingByKey((prev) => ({
      ...prev,
      [key]: true,
    }));
  };

  const handleCancelEdit = (sentence) => {
    const key = sentenceKey(sentence);
    if (!key) return;
    handleResetDraft(sentence);
    setEditingByKey((prev) => ({
      ...prev,
      [key]: false,
    }));
  };

  const handleCreateSingle = async () => {
    const created = await createSentence({
      text: singleText,
      language: singleLanguage,
      view: singleView,
    });
    if (!created) return;

    setSingleText("");
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    await fetchSentences(1, itemsPerPage, {
      sort: sortBy,
      translationFilter,
    });
  };

  const handleCreateBulk = async () => {
    const lines = bulkText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (!lines.length) return;

    const payloads = lines.map((line) => ({
      text: line,
      language: bulkLanguage,
      view: bulkView,
    }));

    const result = await createSentencesBulk(payloads);
    if (!result?.created?.length) return;

    setBulkText("");
    if (currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    await fetchSentences(1, itemsPerPage, {
      sort: sortBy,
      translationFilter,
    });
  };

  const fetchSignsByDocumentIds = async (documentIds = []) => {
    const uniqueDocumentIds = Array.from(new Set((documentIds || []).map((id) => String(id).trim()).filter(Boolean)));
    if (!uniqueDocumentIds.length) return {};

    const base = String(apiBase || "").replace(/\/$/, "");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const map = {};

    const fetchByFilter = async (field, values) => {
      const params = new URLSearchParams();
      values.forEach((value, index) => {
        params.append(`filters[${field}][$in][${index}]`, value);
      });
      params.append("pagination[page]", "1");
      params.append("pagination[pageSize]", String(Math.max(100, values.length)));
      params.append("populate", "sign_videos");

      const fetchFrom = async (endpoint) => {
        const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });
        if (!resp.ok) {
          let message = "Ошибка загрузки жестов перевода";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || errData?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return resp.json();
      };

      let payload;
      try {
        payload = await fetchFrom("/api/signss");
      } catch {
        payload = await fetchFrom("/signss");
      }

      const items = Array.isArray(payload) ? payload : (payload?.data || []);
      items.forEach((item) => {
        const normalized = normalizeSignEntity(item);
        const documentId = normalized?.documentId ?? normalized?.document_id;
        const numericId = Number(normalized?.id);
        if (documentId) map[String(documentId)] = normalized;
        if (Number.isFinite(numericId)) map[String(numericId)] = normalized;
      });
    };

    const documentIdCandidates = uniqueDocumentIds.filter((value) => !/^\d+$/.test(value));
    const numericCandidates = uniqueDocumentIds.filter((value) => /^\d+$/.test(value));

    if (documentIdCandidates.length) {
      await fetchByFilter("documentId", documentIdCandidates);
    }
    if (numericCandidates.length) {
      await fetchByFilter("id", numericCandidates);
    }

    return map;
  };

  const handleStartPlay = async (sentence) => {
    const key = sentenceKey(sentence);
    if (!key) return;

    setPlaybackErrorByKey((prev) => ({ ...prev, [key]: "" }));
    setPlaybackLoadingKey(key);
    setActivePlayback(null);

    try {
      const orderedSignDocumentIds = extractTranslationSignDocumentIds(sentence);
      if (!orderedSignDocumentIds.length) {
        setPlaybackErrorByKey((prev) => ({
          ...prev,
          [key]: "В переводе нет привязанных signs",
        }));
        return;
      }

      const signsByDocumentId = await fetchSignsByDocumentIds(orderedSignDocumentIds);
      const orderedSignVideoIds = [];

      orderedSignDocumentIds.forEach((signDocumentId) => {
        const sign = signsByDocumentId[String(signDocumentId)];
        if (!sign) return;
        const signVideos = normalizeRelationArray(sign?.sign_videos);
        signVideos.forEach((signVideo) => {
          const id = Number(signVideo?.id);
          if (Number.isFinite(id)) orderedSignVideoIds.push(id);
        });
      });

      const uniqueSignVideoIds = Array.from(new Set(orderedSignVideoIds));
      if (!uniqueSignVideoIds.length) {
        setPlaybackErrorByKey((prev) => ({
          ...prev,
          [key]: "У signs нет привязанных видео",
        }));
        return;
      }

      const fetchedMap = await fetchSignVideosByIds(uniqueSignVideoIds, true);
      const signVideosMap = { ...(signVideosById || {}), ...(fetchedMap || {}) };

      const entries = [];
      orderedSignVideoIds.forEach((signVideoId) => {
        const signVideo = signVideosMap[signVideoId] || signVideosMap[String(signVideoId)];
        if (!signVideo) return;

        const files = normalizeMediaFiles(signVideo?.video);
        const mediaFile = files[files.length - 1];
        const url = normalizeMediaUrl(mediaFile?.url, apiBase);
        if (!url) return;

        entries.push({
          url,
          mime: mediaFile?.mime || "video/mp4",
          name: mediaFile?.name || "",
        });
      });

      if (!entries.length) {
        setPlaybackErrorByKey((prev) => ({
          ...prev,
          [key]: "Видео для перевода не найдено",
        }));
        return;
      }

      setActivePlayback({
        key,
        entries,
        index: 0,
      });
    } catch (error) {
      setPlaybackErrorByKey((prev) => ({
        ...prev,
        [key]: error?.message || "Ошибка воспроизведения перевода",
      }));
    } finally {
      setPlaybackLoadingKey("");
    }
  };

  const handleStopPlay = () => {
    setActivePlayback(null);
    setPlaybackLoadingKey("");
  };

  const handlePlaybackEnded = (key) => {
    setActivePlayback((prev) => {
      if (!prev || prev.key !== key) return prev;
      const nextIndex = prev.index + 1;
      if (nextIndex >= prev.entries.length) return null;
      return {
        ...prev,
        index: nextIndex,
      };
    });
  };

  return (
    <main className="main-page">
      <div className="main-card">
        <div className="requests-head">
          <h1>
            <i className="fa-solid fa-quote-right" aria-hidden="true"></i> Предложения
          </h1>
          <div className="requests-head-actions">
            <label className="page-size" htmlFor="sentences-page-size">
              <span>На странице</span>
              <select id="sentences-page-size" value={itemsPerPage} onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <label className="page-size" htmlFor="sentences-sort">
              <span>Сортировка</span>
              <select id="sentences-sort" value={sortBy} onChange={handleSortChange}>
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="page-size" htmlFor="sentences-without-translate">
              <span>Фильтр</span>
              <select
                id="sentences-without-translate"
                  value={translationFilter}
                onChange={(event) => {
                    setTranslationFilter(
                      event.target.value === "translated" || event.target.value === "untranslated"
                        ? event.target.value
                        : "all"
                    );
                  setCurrentPage(1);
                }}
              >
                <option value="all">Все предложения</option>
                  <option value="translated">Переведенные</option>
                  <option value="untranslated">Не переведенные</option>
              </select>
            </label>
            <button
              className="e-btn"
              type="button"
              onClick={() => fetchSentences(currentPage, itemsPerPage, {
                sort: sortBy,
                translationFilter,
              })}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <section className="create-sign-panel">
          <button
            className="e-btn sentences-create-toggle"
            type="button"
            onClick={() => setCreatePanelOpen((prev) => !prev)}
          >
            <i className={`fa-solid ${createPanelOpen ? "fa-chevron-up" : "fa-plus"}`} aria-hidden="true"></i>
            {createPanelOpen ? "Скрыть добавление предложений" : "Добавить предложения"}
          </button>

          {createPanelOpen ? (
            <>
              <div className="sentences-create-mode">
                <button
                  type="button"
                  className={`aside-link ${createMode === "single" ? "active" : ""}`}
                  onClick={() => setCreateMode("single")}
                >
                  По одному
                </button>
                <button
                  type="button"
                  className={`aside-link ${createMode === "bulk" ? "active" : ""}`}
                  onClick={() => setCreateMode("bulk")}
                >
                  Массово
                </button>
              </div>

              {createMode === "single" ? (
                <div className="sentences-create-single">
                  <label>
                    <span>text</span>
                    <textarea
                      value={singleText}
                      onChange={(event) => setSingleText(event.target.value)}
                      rows={3}
                      placeholder="Введите предложение"
                    />
                  </label>
                  <label>
                    <span>language</span>
                    <input
                      type="text"
                      value={singleLanguage}
                      onChange={(event) => setSingleLanguage(event.target.value)}
                      placeholder="Например: ru_RU"
                    />
                  </label>
                  <label>
                    <span>view (optional)</span>
                    <input
                      type="text"
                      value={singleView}
                      onChange={(event) => setSingleView(event.target.value)}
                      placeholder="null если пусто"
                    />
                  </label>
                  <button className="e-btn" type="button" onClick={handleCreateSingle} disabled={sentencesCreateLoading}>
                    <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
                    {sentencesCreateLoading ? "Создаем..." : "Создать предложение"}
                  </button>
                </div>
              ) : (
                <div className="sentences-create-bulk">
                  <label>
                    <span>Предложения (по одному на строку)</span>
                    <textarea
                      value={bulkText}
                      onChange={(event) => setBulkText(event.target.value)}
                      rows={6}
                      placeholder={"Что ты говоришь?\nКак тебя зовут?\nДоброе утро."}
                    />
                  </label>
                  <div className="sentences-create-bulk-meta">
                    <label>
                      <span>language для всех строк</span>
                      <input
                        type="text"
                        value={bulkLanguage}
                        onChange={(event) => setBulkLanguage(event.target.value)}
                        placeholder="Например: ru_RU"
                      />
                    </label>
                    <label>
                      <span>view для всех строк (optional)</span>
                      <input
                        type="text"
                        value={bulkView}
                        onChange={(event) => setBulkView(event.target.value)}
                        placeholder="null если пусто"
                      />
                    </label>
                  </div>
                  <button className="e-btn" type="button" onClick={handleCreateBulk} disabled={sentencesCreateLoading}>
                    <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
                    {sentencesCreateLoading ? "Создаем..." : "Создать массово"}
                  </button>
                </div>
              )}

              {sentencesCreateError ? (
                <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {sentencesCreateError}</p>
              ) : null}
              {sentencesCreateReport ? (
                <p>
                  <i className="fa-solid fa-check" aria-hidden="true"></i>
                  Создано: {sentencesCreateReport.created} | Ошибок: {sentencesCreateReport.failed}
                </p>
              ) : null}
            </>
          ) : null}
        </section>

        {sentencesLoading ? <p><i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> Загрузка предложений...</p> : null}
        {sentencesError ? <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {sentencesError}</p> : null}
        {sentencesMutationError ? <p className="requests-error"><i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i> {sentencesMutationError}</p> : null}

        {!sentencesLoading ? (
          <p>
            <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
            Всего: {sentencesPagination?.total || 0} | Страница: {sentencesPagination?.page || currentPage} из {totalPages}
            {translationFilter === "translated" ? " | Только переведенные" : ""}
            {translationFilter === "untranslated" ? " | Только не переведенные" : ""}
          </p>
        ) : null}

        {!sentencesLoading && !sentences.length ? (
          <p>
            <i className="fa-regular fa-folder-open" aria-hidden="true"></i>
            Пока нет предложений.
          </p>
        ) : null}

        {!!sentences.length ? (
          <div className="requests-list">
            {sentences.map((sentence) => {
              const draft = getDraft(sentence);
              const key = sentenceKey(sentence);
              const isEditing = !!editingByKey[key];
              const hasTranslations = Array.isArray(sentence?.translations) && sentence.translations.length > 0;
              const isPlaying = activePlayback?.key === key;
              const currentPlayEntry = isPlaying ? activePlayback?.entries?.[activePlayback?.index] : null;
              return (
                <article className="requests-item" key={key}>
                  <div className="requests-item-top">
                    <div className="sentence-top-meta">
                      <div className="requests-item-lemma">
                        <i className="fa-solid fa-quote-left" aria-hidden="true"></i>
                        <span>{sentence?.text || `#${sentence?.id || "-"}`}</span>
                      </div>
                      <div className="sentence-meta-inline">
                        <span className="sentence-updated">
                          <i className="fa-regular fa-clock" aria-hidden="true"></i>
                          {formatDateTime(sentence?.updatedAt)}
                        </span>
                        <span className="sentence-id">#{sentence?.id || "-"}</span>
                      </div>
                    </div>
                    <div className="requests-item-actions">
                      <button
                        className="association-open-btn sentence-edit-toggle-btn"
                        type="button"
                        onClick={() => handleStartEdit(sentence)}
                        disabled={isEditing}
                        title="Редактировать"
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true"></i>
                      </button>
                      <button
                        className="association-open-btn"
                        type="button"
                        onClick={() => (isPlaying ? handleStopPlay() : handleStartPlay(sentence))}
                        disabled={playbackLoadingKey === key}
                        title={hasTranslations ? "Проиграть перевод" : "У предложения нет перевода"}
                      >
                        <i className={`fa-solid ${isPlaying ? "fa-stop" : "fa-play"}`} aria-hidden="true"></i>
                        {playbackLoadingKey === key ? "Loading..." : (isPlaying ? "Stop" : "Play")}
                      </button>
                      <span className="status-badge new">{sentence?.language || "-"}</span>
                      <span className={`status-badge ${Array.isArray(sentence?.translations) && sentence.translations.length ? "added" : "new"}`}>
                        translate: {Array.isArray(sentence?.translations) ? sentence.translations.length : 0}
                      </span>
                    </div>
                  </div>

                  {isPlaying && currentPlayEntry ? (
                    <div className="sentence-playback">
                      <video
                        className="sign-video-player"
                        controls
                        autoPlay
                        preload="metadata"
                        playsInline
                        onEnded={() => handlePlaybackEnded(key)}
                        onError={() => handlePlaybackEnded(key)}
                        src={currentPlayEntry.url}
                      >
                        <source src={currentPlayEntry.url} type={currentPlayEntry.mime || "video/mp4"} />
                        Ваш браузер не поддерживает тег video.
                      </video>
                      <p className="sentence-playback-meta">
                        Жест {Number(activePlayback?.index || 0) + 1} из {activePlayback?.entries?.length || 0}
                      </p>
                    </div>
                  ) : null}
                  {playbackErrorByKey[key] ? (
                    <p className="requests-error sentence-playback-error">
                      <i className="fa-solid fa-circle-exclamation" aria-hidden="true"></i>
                      {playbackErrorByKey[key]}
                    </p>
                  ) : null}

                  {!isEditing ? (
                    null
                  ) : (
                    <>
                      <div className="sentence-edit-grid">
                        <label>
                          <span>text</span>
                          <textarea
                            value={draft.text}
                            onChange={(event) => handleDraftChange(sentence, "text", event.target.value)}
                            rows={3}
                          />
                        </label>
                        <label>
                          <span>language</span>
                          <input
                            type="text"
                            value={draft.language}
                            onChange={(event) => handleDraftChange(sentence, "language", event.target.value)}
                          />
                        </label>
                      </div>

                      <div className="sentence-actions">
                        <button className="e-btn" type="button" onClick={() => handleSaveSentence(sentence)} disabled={sentencesMutationLoading}>
                          <i className="fa-solid fa-floppy-disk" aria-hidden="true"></i>
                          {sentencesMutationLoading ? "Сохраняем..." : "Сохранить"}
                        </button>
                        <button className="danger-btn" type="button" onClick={() => handleCancelEdit(sentence)} disabled={sentencesMutationLoading}>
                          <i className="fa-solid fa-rotate-left" aria-hidden="true"></i>
                          Отмена
                        </button>
                      </div>
                    </>
                  )}
                </article>
              );
            })}

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        ) : null}
      </div>
    </main>
  );
};

export default SentencesPage;
